import { useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toastStyles, toastCSS } from "./Toast.style.js";
import {
  getBannerAppearanceConfig,
  TOAST_BANNER_APPEARANCE,
  TOAST_BANNER_MARKER,
} from "./Toast.constants";

const BANNER_MARKER = "▲";

const Toast = ({
  type = "success",
  variant = "toast",
  bannerAppearance = TOAST_BANNER_APPEARANCE.DEFAULT,
  title,
  description,
  show,
  onClose,
  duration = 5000,
}) => {
  const isBanner = variant === "banner";

  useEffect(() => {
    const styleId = "toast-component-styles";
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.textContent = toastCSS;
      document.head.appendChild(styleElement);
    }
  }, []);

  useEffect(() => {
    if (isBanner || !show || duration <= 0) {
      return undefined;
    }

    const timer = setTimeout(() => {
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [isBanner, show, duration, onClose]);

  const handleClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") {
        return;
      }
      onClose?.();
    },
    [onClose]
  );

  const severity = useMemo(() => {
    if (isBanner) return "warning";
    if (type === "success") return "success";
    return "error";
  }, [isBanner, type]);

  const alertTitleStyle = useMemo(
    () => toastStyles.alertTitle(!!description),
    [description]
  );

  const toastIcon = useMemo(() => {
    const Icon = type === "success" ? CheckIcon : ErrorOutlineIcon;

    return (
      <Box sx={toastStyles.iconCircle(type)}>
        <Icon sx={toastStyles.iconGlyph} />
      </Box>
    );
  }, [type]);

  const bannerMarkerType = useMemo(
    () => getBannerAppearanceConfig(bannerAppearance).marker,
    [bannerAppearance]
  );

  const bannerIcon = useMemo(() => {
    const markSx = toastStyles.bannerMark(bannerAppearance);
    if (bannerMarkerType === TOAST_BANNER_MARKER.INFO) {
      return (
        <Box aria-hidden="true" sx={markSx}>
          <InfoOutlinedIcon sx={toastStyles.bannerMarkIcon} />
        </Box>
      );
    }
    return (
      <Box aria-hidden="true" sx={markSx}>
        <span style={toastStyles.bannerMarkTriangle}>{BANNER_MARKER}</span>
      </Box>
    );
  }, [bannerAppearance, bannerMarkerType]);

  const bannerAlertSx = useMemo(
    () => toastStyles.bannerAlert(bannerAppearance),
    [bannerAppearance]
  );

  const bannerTextStyle = useMemo(
    () => toastStyles.bannerText(bannerAppearance),
    [bannerAppearance]
  );

  const alert = (
    <Alert
      severity={severity}
      variant="standard"
      role={isBanner ? "status" : undefined}
      sx={isBanner ? bannerAlertSx : toastStyles.alert(type)}
      onClose={isBanner ? undefined : handleClose}
      icon={isBanner ? bannerIcon : toastIcon}
    >
      {isBanner ? (
        <span style={bannerTextStyle}>{title}</span>
      ) : (
        <>
          <AlertTitle style={alertTitleStyle}>{title}</AlertTitle>
          {description ? (
            <span style={toastStyles.description}>{description}</span>
          ) : null}
        </>
      )}
    </Alert>
  );

  if (isBanner) {
    return show ? alert : null;
  }

  return (
    <Snackbar
      open={show}
      autoHideDuration={duration}
      onClose={handleClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      className="toast-snackbar"
      style={toastStyles.snackbar}
    >
      {alert}
    </Snackbar>
  );
};

Toast.propTypes = {
  type: PropTypes.oneOf(["success", "failure"]),
  variant: PropTypes.oneOf(["toast", "banner"]),
  bannerAppearance: PropTypes.oneOf([
    TOAST_BANNER_APPEARANCE.DEFAULT,
    TOAST_BANNER_APPEARANCE.STATUS,
  ]),
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  duration: PropTypes.number,
};

export default Toast;
